import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { ImageModal } from "../Orders/OrderDetailCard";
import axios from "../../../Utils/axiosInstance";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";
import toast from "react-hot-toast";
import ConfirmActionModal from './../Quotations/ConfirmActionModal';
import ConfirmRefundReceiptModal from "./ConfirmRefundReceiptModal";
import { downloadVisitPassPDF } from "../../../Utils/GenerateVisitPassPdf";

/* ─── Reusable UI Atoms (mirrors OrderDetailCard's local atoms) ───────── */
const SectionCard = ({ title, icon, children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden ${className}`}>
    {title && (
      <div className="px-4 sm:px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/70">
        {icon && <span className="text-sm">{icon}</span>}
        <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{title}</h3>
      </div>
    )}
    <div className="px-4 sm:px-5 py-4">{children}</div>
  </div>
);

const InfoRow = ({ label, value, valueClass = "" }) => (
  <div className="flex flex-col xs:flex-row xs:items-start xs:justify-between gap-0.5 py-2.5 border-b border-gray-100 last:border-0">
    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide flex-shrink-0 min-w-[150px]">{label}</span>
    <span className={`text-sm font-semibold text-right break-words ${valueClass || "text-gray-800"}`}>{value ?? "—"}</span>
  </div>
);

const Chip = ({ emoji, value, colorClass = "bg-white/15 text-white" }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${colorClass}`}>
    {emoji && <span>{emoji}</span>}
    {value}
  </span>
);

const StatusPill = ({ label, tone = "gray" }) => {
  const map = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    gray: "bg-gray-50 text-gray-600 border-gray-200",
    primary: "bg-[#772037]/8 text-[#772037] border-[#772037]/20",
  };
  const dotMap = {
    green: "bg-emerald-500",
    red: "bg-red-500",
    amber: "bg-amber-400 animate-pulse",
    blue: "bg-blue-500",
    gray: "bg-gray-400",
    primary: "bg-[#772037]",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${map[tone]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotMap[tone]}`} />
      {label}
    </span>
  );
};

const OfmAvatar = ({ photo, name, size = "w-11 h-11", textSize = "text-xs" }) => {
  const [imgError, setImgError] = useState(false);
  const initials = (name || "OFM")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join("");

  if (photo && !imgError) {
    return (
      <img
        src={photo}
        alt={name || "OFM"}
        onError={() => setImgError(true)}
        className={`${size} rounded-full object-cover border border-gray-200 shadow-sm flex-shrink-0`}
      />
    );
  }

  return (
    <div
      className={`${size} rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold flex items-center justify-center shadow-sm flex-shrink-0 ${textSize}`}
    >
      {initials || "OFM"}
    </div>
  );
};


/* ─── Progress timeline for a return case ──────────────────────────────
   Verification → Investigation → Decision → Dispatch                     */
const returnStageIndex = (returnDoc) => {
  const decision = returnDoc?.returnDecision?.status;
  const investigation = returnDoc?.investigation?.status;
  const verification = returnDoc?.preInvestigationVerification?.status;
  const dispatch = returnDoc?.returnDispatch?.status;

  if (dispatch === "Delivered") return 4;
  if (decision === "Return_Request_Approved" || decision === "Return_Request_Rejected") return 3;
  if (investigation === "In_Progress" || investigation === "Completed") return 2;
  if (verification === "Approved") return 1;
  return 0;
};

const RETURN_STAGES = [
  { key: "Verification", label: "Pre-Verification", icon: "🔎" },
  { key: "Investigation", label: "Investigation", icon: "🕵️" },
  { key: "Decision", label: "Return Decision", icon: "⚖️" },
  { key: "Dispatch", label: "Return Dispatch", icon: "🚚" },
];

/* ─── Return Dispatch Tracking Steps (mirrors SelectedQuotationView) ──── */
const getReturnTrackingSteps = (status) => [
  { label: "Dispatched", completed: ["Dispatched", "Delivered"].includes(status) },
  { label: "Out for Delivery", completed: ["Dispatched", "Delivered"].includes(status) },
  { label: "Delivered", completed: status === "Delivered" },
];

const ReturnStageTimeline = ({ returnDoc }) => {
  const currentIndex = returnStageIndex(returnDoc);
  const isRejected = returnDoc?.returnDecision?.status === "Return_Request_Rejected";

  return (
    <div className="w-full">
      {/* Desktop */}
      <div className="hidden sm:block">
        <div className="relative flex items-start justify-between">
          <div className="absolute top-5 left-[calc(100%/8)] right-[calc(100%/8)] h-0.5 bg-gray-200" />
          <div
            className={`absolute top-5 h-0.5 transition-all duration-700 ${isRejected ? "bg-red-400" : "bg-gradient-to-r from-[#772037] to-[#CC234F]"
              }`}
            style={{
              left: `calc(100% / ${RETURN_STAGES.length * 2})`,
              width: `${(currentIndex / RETURN_STAGES.length) * 100}%`,
            }}
          />
          {RETURN_STAGES.map((stage, index) => {
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            return (
              <div key={stage.key} className="flex flex-col items-center relative z-10 flex-1 gap-3">
                <div className="relative">
                  {isCurrent && (
                    <span
                      className={`absolute inset-0 rounded-full animate-ping scale-150 ${isRejected ? "bg-red-400/20" : "bg-[#772037]/20"
                        }`}
                    />
                  )}
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 relative z-10
                      ${isCompleted
                        ? "bg-[#772037] border-[#772037] shadow-lg shadow-[#772037]/30"
                        : isCurrent
                          ? isRejected
                            ? "bg-red-500 border-red-500 text-white shadow-lg shadow-red-300/40 scale-110"
                            : "bg-[#772037] border-[#772037] text-white shadow-lg shadow-[#772037]/40 scale-110"
                          : "bg-white border-gray-200"
                      }`}
                  >
                    {isCompleted ? (
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className={`text-base leading-none ${isCurrent ? "animate-bounce text-white" : ""}`}>{stage.icon}</span>
                    )}
                  </div>
                </div>
                <p
                  className={`text-[11px] font-semibold text-center leading-tight px-1
                    ${isCurrent ? (isRejected ? "text-red-600" : "text-[#772037]") : isCompleted ? "text-gray-700" : "text-gray-400"}`}
                >
                  {stage.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile — vertical */}
      <div className="sm:hidden space-y-0">
        {RETURN_STAGES.map((stage, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isLast = index === RETURN_STAGES.length - 1;
          return (
            <div key={stage.key} className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div className="relative">
                  {isCurrent && (
                    <span
                      className={`absolute inset-0 rounded-full animate-ping scale-150 ${isRejected ? "bg-red-400/20" : "bg-[#772037]/20"
                        }`}
                    />
                  )}
                  <div
                    className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center border-2 z-10
                      ${isCompleted
                        ? "bg-[#772037] border-[#772037] shadow-md shadow-[#772037]/30"
                        : isCurrent
                          ? isRejected
                            ? "bg-red-500 border-red-500 text-white shadow-lg shadow-red-300/40 scale-110"
                            : "bg-[#772037] border-[#772037] text-white shadow-lg shadow-[#772037]/40 scale-110"
                          : "bg-white border-gray-200"
                      }`}
                  >
                    {isCompleted ? (
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className={`text-sm ${isCurrent ? "text-white animate-bounce" : ""}`}>{stage.icon}</span>
                    )}
                  </div>
                </div>
                {!isLast && (
                  <div
                    className={`w-0.5 flex-1 min-h-[32px] my-0.5 rounded-full
                      ${isCompleted ? "bg-gradient-to-b from-[#772037] to-[#CC234F]" : isCurrent ? "bg-gradient-to-b from-[#772037] to-gray-200" : "bg-gray-200"}`}
                  />
                )}
              </div>
              <div className="pb-4 pt-1.5">
                <p
                  className={`text-sm font-semibold ${isCurrent ? (isRejected ? "text-red-600 scale-105" : "text-[#772037] scale-105") : isCompleted ? "text-gray-700" : "text-gray-400"
                    }`}
                >
                  {stage.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Main Component ────────────────────────────────────────────────── */
const ReturnDetailCard = ({ returnDoc, refreshReturn }) => {
  const [showAllRefunds, setShowAllRefunds] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundModalImages, setRefundModalImages] = useState([]);
  const [refundModalIndex, setRefundModalIndex] = useState(0);

  const [confirmRefundOpen, setConfirmRefundOpen] = useState(false);
  const [refundToConfirm, setRefundToConfirm] = useState(null);

  // ── Visit Pass State ────────────────────────────────────────────────
  const [fetchedPass, setFetchedPass] = useState(null);
  const [downloadingPass, setDownloadingPass] = useState(false);

  useEffect(() => {
    const fetchDisputePass = async () => {
      const orderId = returnDoc?.orderId?._id || (typeof returnDoc?.orderId === "string" ? returnDoc?.orderId : null);
      if (!returnDoc?.clientVisitPassId && orderId) {
        try {
          const { data } = await axios.get(`/client-visit-pass/order/${orderId}`);
          if (data?.success) {
            setFetchedPass(
              data.passes?.disputePass ||
              (data.data?.passType === "Dispute" ? data.data : null) ||
              null
            );
          }
        } catch (e) {
          console.warn("Failed to fetch visit pass for return:", e);
        }
      }
    };
    fetchDisputePass();
  }, [returnDoc]);

  // ── Initiate Return Dispatch (Buyer action) ──────────────────────────
  const [dispatching, setDispatching] = useState(false);
  const [confirmDispatchOpen, setConfirmDispatchOpen] = useState(false);

  // ── Return Dispatch section auto-scroll & highlight ─────────────────
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const dispatchSectionRef = useRef(null);
  const [highlightDispatch, setHighlightDispatch] = useState(false);
  const hasScrolledRef = useRef(false);

  const isDispatchTarget =
    searchParams.get("section") === "return-dispatch" ||
    searchParams.get("section") === "dispatch" ||
    location.state?.scrollTo === "return-dispatch";

  useEffect(() => {
    hasScrolledRef.current = false;
  }, [location.key, returnDoc?._id]);

  useEffect(() => {
    if (isDispatchTarget && returnDoc && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      const timer = setTimeout(() => {
        if (dispatchSectionRef.current) {
          dispatchSectionRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          setHighlightDispatch(true);

          const fadeTimer = setTimeout(() => {
            setHighlightDispatch(false);
          }, 3500);

          return () => clearTimeout(fadeTimer);
        }
      }, 350);

      return () => clearTimeout(timer);
    }
  }, [returnDoc?._id, isDispatchTarget, location.key]);

  useLockBodyScroll(refundModalOpen || confirmDispatchOpen);

  const openRefundProofModal = (images, index) => {
    setRefundModalImages(images);
    setRefundModalIndex(index);
    setRefundModalOpen(true);
  };

  const openConfirmRefundModal = (refund) => {
    setRefundToConfirm(refund);
    setConfirmRefundOpen(true);
  };

  const handleInitiateDispatch = async () => {
    if (!returnDoc?._id) return;
    try {
      setDispatching(true);
      const { data } = await axios.patch(`/return/${returnDoc._id}/dispatch`, {});
      if (data?.success) {
        toast.success("Return dispatch initiated ✅");
        setConfirmDispatchOpen(false);
        await refreshReturn?.();
      } else {
        toast.error(data?.message || "Failed to initiate dispatch");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setDispatching(false);
    }
  };

  if (!returnDoc) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center p-8">
        <div className="w-16 h-16 rounded-2xl bg-[#772037]/8 flex items-center justify-center mb-4">
          <span className="text-3xl">↩️</span>
        </div>
        <p className="text-gray-400 font-medium text-sm">Select a return to view details</p>
      </div>
    );
  }

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—";
  const fmtDateTime = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      : "—";
  const fmtAmount = (n) => `₹${Number(n || 0).toLocaleString()}`;

  const decisionTone =
    returnDoc.returnDecision?.status === "Return_Request_Approved"
      ? "green"
      : returnDoc.returnDecision?.status === "Return_Request_Rejected"
        ? "red"
        : "amber";

  const verificationTone =
    returnDoc.preInvestigationVerification?.status === "Approved"
      ? "green"
      : returnDoc.preInvestigationVerification?.status === "Rejected"
        ? "red"
        : "amber";

  const hasSupplierPenalty = returnDoc.supplierPenalty?.totalAmount > 0;
  const hasBuyerObligation = returnDoc.buyerObligation?.ofmAllowanceAmount > 0;
  const refunds = returnDoc.refunds || [];
  const visibleRefunds = showAllRefunds ? refunds : refunds.slice(0, 2);

  const dispatchStatusColor = (s) => {
    switch (s) {
      case "Dispatched":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "Delivered":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      default:
        return "bg-gray-50 text-gray-600 border-gray-200";
    }
  };

  const paymentStatusColor = (s) => {
    switch (s) {
      case "Paid":
      case "Processed":
      case "Received":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "Failed":
        return "bg-red-50 text-red-700 border-red-200";
      case "Waived":
        return "bg-gray-50 text-gray-600 border-gray-200";
      default:
        return "bg-amber-50 text-amber-700 border-amber-200";
    }
  };

  const assignedOFMName =
    returnDoc.assignedOFM?.name ||
    [returnDoc.assignedOFM?.firstName, returnDoc.assignedOFM?.lastName].filter(Boolean).join(" ") ||
    null;

  const canInitiateDispatch =
    returnDoc.preInvestigationVerification?.status === "Approved" &&
    returnDoc.returnDecision?.status === "Return_Request_Approved" &&
    (!returnDoc.returnDispatch?.status || returnDoc.returnDispatch.status === "Not_Initiated");

  return (
    <div className="h-full overflow-y-auto space-y-4 pb-6 pr-0.5">
      {/* ══ 1. HEADER BANNER ══════════════════════════════ */}
      <div className="bg-gradient-to-br shineEffect from-[#772037] to-[#CC234F] rounded-2xl p-4 sm:p-5 text-white shadow-lg shadow-[#772037]/20">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-1">Return Case</p>
            <h2 className="text-lg sm:text-xl font-bold tracking-tight">
              {returnDoc.uniqueCaseId || `#${returnDoc._id?.slice(-6)}`}
            </h2>
            <p className="text-xs text-white/60 mt-0.5">Raised on {fmtDate(returnDoc.createdAt)}</p>
          </div>
          <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 flex-wrap">
            <span
              className={`px-3 py-1.5 rounded-full text-xs font-bold ${returnDoc.returnDecision?.status === "Return_Request_Approved"
                ? "bg-emerald-400/20 text-emerald-100 border border-emerald-200/40"
                : returnDoc.returnDecision?.status === "Return_Request_Rejected"
                  ? "bg-red-400/20 text-red-100 border border-red-200/40"
                  : "bg-white/20 text-white border border-white/30"
                }`}
            >
              {returnDoc.returnDecision?.status?.replace(/_/g, " ") || "Pending"}
            </span>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Chip
            emoji={returnDoc.returnType === "Full_Order" ? "📦" : "🧩"}
            value={returnDoc.returnType === "Full_Order" ? "Full Order Return" : "Partial Return"}
          />
          {returnDoc.orderId?.orderNumber && <Chip emoji="🧾" value={returnDoc.orderId.orderNumber} />}
          {returnDoc.quotationId?.quotationUniqueNumber && (
            <Chip emoji="💰" value={returnDoc.quotationId.quotationUniqueNumber} />
          )}
        </div>
      </div>

      {/* ══ 2. RETURN PROGRESS ═════════════════════════════ */}
      <SectionCard title="Return Progress" icon="🗺️">
        <ReturnStageTimeline returnDoc={returnDoc} />
        <div className="mt-5 flex justify-center">
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-[#772037]/6 text-[#772037] border border-[#772037]/20">
            <span className="w-2 h-2 rounded-full bg-[#772037] animate-ping" />
            Currently: {RETURN_STAGES[Math.min(returnStageIndex(returnDoc), 3)]?.label}
          </span>
        </div>
      </SectionCard>

      {/* ══ 3. RETURN DETAILS ══════════════════════════════ */}
      <SectionCard title="Return Details" icon="📋">
        <InfoRow label="Return Type" value={returnDoc.returnType === "Full_Order" ? "Full Order" : "Partial Order"} />
        {returnDoc.returnType === "Partial" && (
          <>
            <InfoRow label="Packets To Return" value={returnDoc.partialReturnPackets ?? "—"} />
            <InfoRow
              label="Weight Per Pack"
              value={
                returnDoc.packSpecification?.weightOfEachPack
                  ? `${returnDoc.packSpecification.weightOfEachPack} ${returnDoc.packSpecification?.unitOfWeight || "Kg"}`
                  : "—"
              }
            />
            <InfoRow label="Packs To Be Returned" value={returnDoc.packSpecification?.numberOfPacksToBeReturned ?? "—"} />
          </>
        )}
        <InfoRow label="Order" value={returnDoc.orderId?.orderNumber || "—"} />
        <InfoRow label="Quotation" value={returnDoc.quotationId?.quotationUniqueNumber || returnDoc.quotationId?.quotationNumber || "—"} />
        {returnDoc.quotationId?.totalAmount != null && (
          <InfoRow label="Quotation Amount" value={fmtAmount(returnDoc.quotationId.totalAmount)} />
        )}
        <div className="mt-3 bg-gray-50 border border-gray-100 rounded-xl p-3.5">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Reason for Return</p>
          <p className="text-sm text-gray-700 leading-relaxed">{returnDoc.reason || "—"}</p>
        </div>
      </SectionCard>

      {/* ══ 4. PRE-INVESTIGATION VERIFICATION ═════════════ */}
      <SectionCard title="Pre-Investigation Verification" icon="🔎">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-400 font-medium">Verification Status</span>
          <StatusPill label={returnDoc.preInvestigationVerification?.status === "Approved" ? "Approved by QQT" : "Pending QQT Approval"} tone={verificationTone} />
        </div>
        <InfoRow label="Verified At" value={fmtDateTime(returnDoc.preInvestigationVerification?.verifiedAt)} />
        {returnDoc.preInvestigationVerification?.note && (
          <div
            className={`mt-3 rounded-xl p-3.5 text-sm leading-relaxed border ${verificationTone === "green"
              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
              : verificationTone === "red"
                ? "bg-red-50 text-red-800 border-red-200"
                : "bg-amber-50 text-amber-800 border-amber-200"
              }`}
          >
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-1">Verification Note</p>
            {returnDoc.preInvestigationVerification.note}
          </div>
        )}
      </SectionCard>

      {/* ══ 5. INVESTIGATION ═══════════════════════════════ */}
      <SectionCard title="Investigation" icon="🕵️">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-400 font-medium">Investigation Status</span>
          <StatusPill
            label={(returnDoc.investigation?.status || "Not_Started").replace(/_/g, " ")}
            tone={
              returnDoc.investigation?.status === "Completed"
                ? "green"
                : returnDoc.investigation?.status === "In_Progress"
                  ? "blue"
                  : "gray"
            }
          />
        </div>
        <InfoRow label="Started At" value={fmtDateTime(returnDoc.investigation?.startedAt)} />
        <InfoRow label="Completed At" value={fmtDateTime(returnDoc.investigation?.completedAt)} />
        {assignedOFMName && (
          <div className="mt-3 flex items-center gap-3 p-3 rounded-xl bg-purple-50 border border-purple-100">
            <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0">
              <span className="text-sm">🧑‍💼</span>
            </div>
            <div>
              <p className="text-[10px] font-black text-purple-400 uppercase tracking-widest">Assigned OFM</p>
              <p className="text-sm font-bold text-purple-900">{assignedOFMName}</p>
              {returnDoc.assignedOFM?.email && <p className="text-xs text-purple-500">{returnDoc.assignedOFM.email}</p>}
            </div>
          </div>
        )}

        {/* ── Client Visit Pass (Investigation Section) ── */}
        {(() => {
          const rawPass =
            (typeof returnDoc.clientVisitPassId === "object" ? returnDoc.clientVisitPassId : null) ||
            returnDoc.clientVisitPass ||
            fetchedPass;
          const pass = (rawPass && (rawPass.passType === "Dispute" || !rawPass.passType)) ? rawPass : null;

          if (pass) {
            return (
              <div className="mt-4 rounded-xl border border-amber-300 bg-gradient-to-br from-amber-50/90 via-white to-orange-50/60 p-4 shadow-sm space-y-3">
                <div className="flex items-center justify-between border-b border-amber-200/70 pb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                      ⚠️
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-xs font-bold text-gray-900">Dispute Resolution Visit Pass</h4>
                        <span className="px-2 py-0.5 rounded text-[9px] font-extrabold uppercase border bg-amber-100 text-amber-900 border-amber-300">
                          Dispute Stage Pass
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-500 font-medium">
                        Official Verification Pass Issued by PCM Exports Admin
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 rounded-full bg-white border border-gray-200 text-gray-800 text-[10px] font-mono font-bold uppercase">
                      {pass.passNumber || "ACTIVE PASS"}
                    </span>
                    <button
                      type="button"
                      disabled={downloadingPass}
                      onClick={async () => {
                        try {
                          setDownloadingPass(true);
                          await downloadVisitPassPDF(pass);
                          toast.success("Visit Pass PDF downloaded successfully");
                        } catch (err) {
                          toast.error("Failed to download pass PDF");
                        } finally {
                          setDownloadingPass(false);
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-lg text-white bg-amber-600 hover:bg-amber-700 shadow-sm transition-all"
                    >
                      <span>{downloadingPass ? "Preparing..." : "📥 Download Pass PDF"}</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs">
                  {(() => {
                    const ofmPhoto =
                      pass.ofmDetails?.photo ||
                      pass.ofmId?.profilePic ||
                      pass.ofmId?.identityVerification?.aadhaar?.photoUrl ||
                      null;
                    const ofmName =
                      pass.ofmDetails?.name ||
                      assignedOFMName ||
                      (pass.ofmId
                        ? `${pass.ofmId.firstName || ""} ${pass.ofmId.lastName || ""}`.trim()
                        : "OFM Representative");

                    return (
                      <div className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm flex items-center gap-3">
                        <OfmAvatar photo={ofmPhoto} name={ofmName} size="w-10 h-10" />
                        <div className="space-y-0.5 min-w-0">
                          <p className="text-[9px] uppercase font-bold text-gray-400 tracking-wider">Assigned OFM Agent</p>
                          <p className="text-xs font-extrabold text-gray-900 truncate">{ofmName}</p>
                          <p className="text-[11px] text-gray-600">📞 Contact: <span className="font-semibold text-gray-800">{pass.ofmDetails?.phone || pass.ofmId?.phone || "N/A"}</span></p>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="pt-2 border-t border-amber-200/70 flex flex-wrap items-center justify-between gap-2 text-[10px] text-gray-600">
                  <span>🏢 Address: <strong className="text-gray-800">{pass.clientDetails?.address || "Delivery Site / Warehouse"}</strong></span>
                  <span>Valid Until: <strong className="text-amber-700 font-bold">{pass.validity?.toDate ? new Date(pass.validity.toDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "N/A"}</strong></span>
                </div>
              </div>
            );
          }

          return (
            <div className="mt-3 p-3 rounded-xl bg-gray-50 border border-gray-100 flex items-center gap-2 text-xs text-gray-500">
              <span>ℹ️</span>
              <span>Client Visit Pass will be issued by admin once OFM verification is initiated.</span>
            </div>
          );
        })()}
      </SectionCard>

      {/* ══ 6. RETURN DECISION ═════════════════════════════ */}
      <SectionCard title="Return Decision" icon="⚖️">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-400 font-medium">Decision</span>
          <StatusPill label={returnDoc.returnDecision?.status === "Return_Request_Pending" ? "Return Decision Pending(OFM Verification)" : returnDoc.returnDecision?.status === "Return_Request_Approved" ? "Return Decision Approved (After OFM Verification)" : returnDoc.returnDecision?.status === "Return_Request_Rejected" ? "Return Decision Rejected (After OFM Verification)" : "—"} tone={decisionTone} />
        </div>
        <InfoRow label="Decided At" value={fmtDateTime(returnDoc.returnDecision?.decidedAt)} />
        {returnDoc.returnDecision?.note && (
          <div
            className={`mt-3 rounded-xl p-3.5 text-sm leading-relaxed border ${decisionTone === "green"
              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
              : decisionTone === "red"
                ? "bg-red-50 text-red-800 border-red-200"
                : "bg-amber-50 text-amber-800 border-amber-200"
              }`}
          >
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-1">Decision Note</p>
            {returnDoc.returnDecision.note}
          </div>
        )}
      </SectionCard>

      {/* ══ 7. FINANCIAL OBLIGATIONS (only if applicable) ══ */}
      {(hasBuyerObligation) && (
        <SectionCard label="Financial Obligations" title="Financial Obligations" icon="💳">
          {/* {hasSupplierPenalty && (
            <div className="mb-4 rounded-xl border border-red-100 bg-red-50/50 p-3.5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-red-600 uppercase tracking-widest">Supplier Penalty</p>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${paymentStatusColor(returnDoc.supplierPenalty?.status)}`}>
                  {returnDoc.supplierPenalty?.status}
                </span>
              </div>
              <InfoRow label="Refund Amount" value={fmtAmount(returnDoc.supplierPenalty?.refundAmount)} />
              <InfoRow label="OFM Allowance" value={fmtAmount(returnDoc.supplierPenalty?.ofmAllowanceAmount)} />
              <InfoRow label="Total Amount" value={fmtAmount(returnDoc.supplierPenalty?.totalAmount)} valueClass="text-red-600" />
              <InfoRow label="Due Date" value={fmtDate(returnDoc.supplierPenalty?.dueDate)} />
            </div>
          )} */}
          {hasBuyerObligation && (
            <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3.5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-amber-600 uppercase tracking-widest">Your Obligation (OFM Allowance)</p>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${paymentStatusColor(returnDoc.buyerObligation?.status)}`}>
                  {returnDoc.buyerObligation?.status}
                </span>
              </div>
              <InfoRow label="Amount" value={fmtAmount(returnDoc.buyerObligation?.ofmAllowanceAmount)} valueClass="text-amber-700" />
              <InfoRow label="Due Date" value={fmtDate(returnDoc.buyerObligation?.dueDate)} />
              <InfoRow label="Paid At" value={fmtDateTime(returnDoc.buyerObligation?.paidAt)} />
            </div>
          )}
        </SectionCard>
      )}

      {/* ══ 8. RETURN DISPATCH ═════════════════════════════ */}
      {returnDoc.returnDispatch?.status && returnDoc.returnDispatch.status !== "Not_Initiated" ? (
        <div
          ref={dispatchSectionRef}
          id="return-dispatch-section"
          className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md hover:border-[#772037]/30 transition-all duration-700 group relative ${
            highlightDispatch
              ? "ring-4 ring-[#772037] ring-offset-2 shadow-2xl scale-[1.01]"
              : ""
          }`}
        >
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#772037]/5 via-transparent to-[#CC234F]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#772037]/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:w-32 group-hover:h-32 pointer-events-none" />

          {/* HEADER */}
          <div className="px-4 sm:px-5 py-3.5 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between gap-3 relative z-10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-[#772037]/20 to-[#CC234F]/20 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">🚛</span>
              </div>
              <div>
                <h4 className="font-bold text-gray-800 text-sm">Return Dispatch Status</h4>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  {returnDoc.returnType === "Full_Order" ? "Full Order Return" : "Partial Return"}
                </p>
              </div>
            </div>
            {highlightDispatch && (
              <span className="text-[10px] font-bold bg-[#772037] text-white px-2.5 py-1 rounded-full shadow animate-bounce">
                Return Dispatch 👇
              </span>
            )}
          </div>

          {/* TIMELINE */}
          <div className="px-4 sm:px-5 pt-5 pb-3 relative z-10">
            <div className="relative flex justify-between items-start">
              {getReturnTrackingSteps(returnDoc.returnDispatch.status).map((step, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center relative">
                  {idx !== 0 && (
                    <div className="absolute left-[-50%] top-3.5 sm:top-4 w-full h-1 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-700 ${step.completed ? "bg-gradient-to-r from-green-400 via-green-500 to-green-600" : "bg-gray-200"
                          }`}
                      />
                    </div>
                  )}
                  <div
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold z-10 transition-all duration-500 relative ${step.completed
                      ? "bg-gradient-to-br from-green-400 to-green-600 text-white scale-110 shadow-lg shadow-green-500/50"
                      : "bg-gray-200 text-gray-500"
                      }`}
                  >
                    {step.completed && (
                      <div className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-20" />
                    )}
                    {step.completed ? "✓" : ""}
                  </div>
                  <p
                    className={`text-[10px] sm:text-xs mt-2 text-center leading-tight px-1 transition-all duration-500 ${step.completed ? "text-green-600 font-semibold" : "text-gray-400"
                      }`}
                  >
                    {step.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* DETAILS */}
          <div className="px-4 sm:px-5 pb-4 space-y-3 relative z-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
              {returnDoc.returnDispatch?.dispatchedAt && (
                <div className="flex items-center gap-2 py-2 border-t border-gray-50">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                  <span className="text-xs text-gray-400 flex-shrink-0">Dispatched:</span>
                  <span className="text-xs font-semibold text-gray-700 truncate">
                    {fmtDateTime(returnDoc.returnDispatch.dispatchedAt)}
                  </span>
                </div>
              )}
              {returnDoc.returnDispatch?.deliveredAt && (
                <div className="flex items-center gap-2 py-2 border-t border-gray-50">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                  <span className="text-xs text-gray-400 flex-shrink-0">Delivered:</span>
                  <span className="text-xs font-semibold text-gray-700 truncate">
                    {fmtDateTime(returnDoc.returnDispatch.deliveredAt)}
                  </span>
                </div>
              )}
            </div>

            {(returnDoc.returnDispatch?.AWB_Number || returnDoc.returnDispatch?.trackingUrl) && (
              <div className="border-t border-gray-100 pt-3 space-y-2.5">
                {returnDoc.returnDispatch?.AWB_Number && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 text-sm">📦</span>
                      <span className="text-xs font-semibold text-blue-700">AWB Number</span>
                    </div>
                    <span className="text-xs font-mono font-bold text-blue-900 bg-white px-3 py-1.5 rounded-lg border border-blue-200">
                      {returnDoc.returnDispatch.AWB_Number}
                    </span>
                  </div>
                )}
                {returnDoc.returnDispatch?.trackingUrl && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-600 text-sm">🔗</span>
                      <span className="text-xs font-semibold text-emerald-700">Tracking Link</span>
                    </div>

                    <a href={returnDoc.returnDispatch.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-emerald-600 hover:text-emerald-700 hover:underline flex items-center gap-1 transition"
                    >
                      Open Tracking
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                )}
              </div>
            )}

            {returnDoc.returnDispatch?.pickUpAddress && (
              <div className="rounded-xl bg-gray-50 border border-gray-100 p-3.5">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Pick-up Address (Your)</p>
                <p className="text-xs text-gray-700 leading-relaxed">
                  {[
                    returnDoc.returnDispatch.pickUpAddress?.addressLine1,
                    returnDoc.returnDispatch.pickUpAddress?.city,
                    returnDoc.returnDispatch.pickUpAddress?.state,
                    returnDoc.returnDispatch.pickUpAddress?.pincode,
                  ]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        canInitiateDispatch && (
          <div
            ref={dispatchSectionRef}
            id="return-dispatch-section"
            className={`transition-all duration-700 rounded-2xl ${
              highlightDispatch
                ? "ring-4 ring-[#772037] ring-offset-2 shadow-2xl scale-[1.01]"
                : ""
            }`}
          >
            <SectionCard title="Return Dispatch" icon="🚛">
              <div
                className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 transition-all duration-700 ${
                  highlightDispatch
                    ? "border-[#772037] bg-gradient-to-r from-[#772037]/15 via-amber-50/60 to-[#CC234F]/10 ring-2 ring-[#772037]/40 shadow-md"
                    : "border-[#772037]/20 bg-[#772037]/5"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#772037]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">📤</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-[#772037]">Ready to Dispatch Return</p>
                      {highlightDispatch && (
                        <span className="text-[10px] font-bold bg-[#772037] text-white px-2.5 py-0.5 rounded-full shadow animate-bounce">
                          Dispatch Here 👇
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Your return has been approved. Please dispatch the item(s) back for processing.
                    </p>
                  </div>
                </div>
                <button
                  disabled={dispatching}
                  onClick={() => setConfirmDispatchOpen(true)}
                  className={`flex-shrink-0 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#772037] to-[#CC234F] text-white font-bold text-sm hover:opacity-90 disabled:opacity-50 transition shadow-lg shadow-[#772037]/20 cursor-pointer ${
                    highlightDispatch ? "animate-pulse scale-105" : ""
                  }`}
                >
                  {dispatching ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>🚀 Initiate Dispatch</>
                  )}
                </button>
              </div>
            </SectionCard>
          </div>
        )
      )}

      {/* ══ 9. REFUNDS ═════════════════════════════════════ */}
      {
        refunds.length > 0 && (
          <SectionCard title="Refunds" icon="💸">
            <div className="space-y-3">
              {visibleRefunds.map((refund, i) => {
                const proofImages = (refund.proofOfTransaction || []).map((p) => p.url).filter(Boolean);
                const receivedImages = (refund.receivedProof || []).map((p) => p.url).filter(Boolean);

                return (
                  <div key={refund._id || i} className="rounded-xl border border-gray-100 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-700">
                          Refund #{i + 1} · {refund.refundFor === "Partial" ? "Partial" : "Full Order"}
                        </span>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${paymentStatusColor(refund.status)}`}>
                        {refund.status}
                      </span>
                    </div>

                    {/* Core details */}
                    <div className="px-4 py-1">
                      <InfoRow label="Refund Amount" value={fmtAmount(refund.refundAmount)} valueClass="text-[#772037]" />
                      <InfoRow label="Transaction ID" value={refund.transactionId || "—"} />
                      <InfoRow label="Processed At" value={fmtDateTime(refund.processedAt || refund.processedDate)} />
                      {refund.receivedAt && <InfoRow label="Received At" value={fmtDateTime(refund.receivedAt)} />}
                      {refund.remarks && <InfoRow label="Remarks" value={refund.remarks} />}
                    </div>

                    {/* Confirm receipt CTA — buyer side */}
                    {refund.status === "Processed" && (
                      <div className="px-4 pb-3">
                        <button
                          type="button"
                          onClick={() => openConfirmRefundModal(refund)}
                          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold transition-colors"
                        >
                          ✓ Confirm Receipt
                        </button>
                      </div>
                    )}

                    {/* Transaction method details */}
                    {refund.transactionDetails && (
                      refund.transactionDetails.method ||
                      refund.transactionDetails.bankName ||
                      refund.transactionDetails.accountNumber ||
                      refund.transactionDetails.referenceNumber ||
                      refund.transactionDetails.amount
                    ) && (
                        <div className="mx-4 mb-3 rounded-lg bg-gray-50 border border-gray-100 p-3">
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                            Transaction Method
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                            {refund.transactionDetails.method && (
                              <InfoRow label="Method" value={refund.transactionDetails.method.replace(/_/g, " ")} />
                            )}
                            {refund.transactionDetails.amount != null && (
                              <InfoRow label="Amount" value={fmtAmount(refund.transactionDetails.amount)} />
                            )}
                            {refund.transactionDetails.bankName && (
                              <InfoRow label="Bank Name" value={refund.transactionDetails.bankName} />
                            )}
                            {refund.transactionDetails.accountNumber && (
                              <InfoRow label="Account Number" value={refund.transactionDetails.accountNumber} />
                            )}
                            {refund.transactionDetails.referenceNumber && (
                              <InfoRow label="Reference Number" value={refund.transactionDetails.referenceNumber} />
                            )}
                          </div>
                        </div>
                      )}

                    {/* Proof of transaction (admin-uploaded) */}
                    {proofImages.length > 0 && (
                      <div className="mx-4 mb-3">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                          Proof of Transaction
                        </p>
                        <div className="flex flex-wrap gap-2.5">
                          {proofImages.map((url, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => openRefundProofModal(proofImages, idx)}
                              className="group relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 hover:border-[#772037]/40 transition-all hover:shadow-md bg-gray-100"
                            >
                              <img
                                src={url}
                                alt={`Transaction proof ${idx + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                              <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <span className="bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">View</span>
                              </div>
                            </button>
                          ))}
                        </div>
                        {refund.proofOfTransaction?.[0]?.description && (
                          <p className="text-[11px] text-gray-400 mt-1.5">{refund.proofOfTransaction[0].description}</p>
                        )}
                      </div>
                    )}

                    {/* Buyer-side received proof */}
                    {receivedImages.length > 0 && (
                      <div className="mx-4 mb-4">
                        <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-2">
                          Your Received Proof
                        </p>
                        <div className="flex flex-wrap gap-2.5">
                          {receivedImages.map((url, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => openRefundProofModal(receivedImages, idx)}
                              className="group relative w-16 h-16 rounded-lg overflow-hidden border border-emerald-200 hover:border-emerald-400 transition-all hover:shadow-md bg-gray-100"
                            >
                              <img
                                src={url}
                                alt={`Received proof ${idx + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                              <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <span className="bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">View</span>
                              </div>
                            </button>
                          ))}
                        </div>
                        {refund.receivedProof?.[0]?.description && (
                          <p className="text-[11px] text-gray-400 mt-1.5">{refund.receivedProof[0].description}</p>
                        )}
                      </div>
                    )}

                    {!proofImages.length && !receivedImages.length && <div className="pb-3" />}
                  </div>
                );
              })}
            </div>

            {refunds.length > 2 && (
              <button
                onClick={() => setShowAllRefunds((prev) => !prev)}
                className="w-full mt-3 border border-gray-200 rounded-xl px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-all text-xs font-semibold text-gray-600"
              >
                {showAllRefunds ? "Show Less" : `Show ${refunds.length - 2} More`}
              </button>
            )}
          </SectionCard>
        )
      }

      {/* ── Refund Proof Image Modal ── */}
      {refundModalOpen && (
        <ImageModal
          images={refundModalImages}
          videos={[]}
          initialIndex={refundModalIndex}
          onClose={() => setRefundModalOpen(false)}
        />
      )}

      {/* ── Initiate Dispatch Confirmation ── */}
      <ConfirmActionModal
        open={confirmDispatchOpen}
        onClose={() => setConfirmDispatchOpen(false)}
        onConfirm={handleInitiateDispatch}
        loading={dispatching}
        title="Initiate Return Dispatch"
        description="Are you sure you want to initiate dispatch for this return? Make sure the item(s) are packed and ready for pickup."
        confirmText="Yes, Initiate Dispatch"
      />

      {/* ── Confirm Refund Receipt ── */}
      <ConfirmRefundReceiptModal
        isOpen={confirmRefundOpen}
        onClose={() => setConfirmRefundOpen(false)}
        refund={refundToConfirm}
        onSuccess={refreshReturn}
      />

    </div>
  );
};

export default ReturnDetailCard;